import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Paper,
  Alert,
  SelectChangeEvent,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Code as CodeIcon,
  Tune as TuneIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { RJSFSchema } from "@rjsf/utils";
import JsonSchemaAnswerRenderer from "./JsonSchemaAnswerRenderer";

interface HybridSchemaEditorProps {
  value: RJSFSchema;
  onChange: (schema: RJSFSchema) => void;
  disabled?: boolean;
}

type EditorMode = "simple" | "advanced" | "preview";

type SimpleFieldType = "text" | "number" | "boolean" | "date" | "time" | "multiselect" | "dropdown";

/**
 * Hybrid schema editor that provides:
 * 1. Simple mode - Easy UI for common field types
 * 2. Advanced mode - Direct JSON editing for complex schemas
 * 3. Preview mode - See how the form will render
 */
const HybridSchemaEditor: React.FC<HybridSchemaEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [mode, setMode] = useState<EditorMode>("simple");
  const [simpleType, setSimpleType] = useState<SimpleFieldType>("text");
  const [options, setOptions] = useState<string[]>([]);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [modeChangeDialogOpen, setModeChangeDialogOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<EditorMode | null>(null);

  // Initialize from existing schema
  useEffect(() => {
    if (value && typeof value === "object") {
      const detectedType = detectSimpleType(value);
      if (detectedType) {
        // Schema matches a simple type - start in simple mode
        setMode("simple");
        setSimpleType(detectedType);
        if (detectedType === "multiselect" && value.properties) {
          setOptions(Object.keys(value.properties));
        } else if (detectedType === "dropdown" && value.enum) {
          setOptions(value.enum as string[]);
        }
      } else if (Object.keys(value).length > 0) {
        // Schema is complex - start in advanced mode
        setMode("advanced");
      }
    }
    setJsonText(JSON.stringify(value || {}, null, 2));
  }, [value]);

  // Detect if schema matches a simple type
  const detectSimpleType = (schema: RJSFSchema): SimpleFieldType | null => {
    if (!schema.type) return null;

    if (schema.type === "string") {
      if (schema.format === "date") return "date";
      if (schema.format === "time") return "time";
      if (schema.enum) return "dropdown";
      return "text";
    }
    if (schema.type === "number") return "number";
    if (schema.type === "boolean") return "boolean";
    if (schema.type === "object" && schema.properties) {
      const props = Object.values(schema.properties);
      if (props.every((p: any) => p.type === "boolean")) {
        return "multiselect";
      }
    }
    return null;
  };

  // Convert simple type to JSON schema
  const simpleTypeToSchema = useCallback((): RJSFSchema => {
    switch (simpleType) {
      case "text":
        return { type: "string" };
      case "number":
        return { type: "number" };
      case "boolean":
        return { type: "boolean" };
      case "date":
        return { type: "string", format: "date" };
      case "time":
        return { type: "string", format: "time" };
      case "dropdown":
        return {
          type: "string",
          enum: options.filter(Boolean),
        };
      case "multiselect":
        const schema: RJSFSchema = {
          type: "object",
          additionalProperties: false,
          properties: {},
          required: [],
        };
        options.filter(Boolean).forEach((option) => {
          (schema.properties as any)[option] = { type: "boolean" };
          (schema.required as string[]).push(option);
        });
        return schema;
    }
  }, [simpleType, options]);

  // Track when user makes changes in simple mode
  const prevSimpleTypeRef = useRef(simpleType);
  const prevOptionsRef = useRef(options);
  
  useEffect(() => {
    // Only update if we're in simple mode AND the user actually changed something
    if (mode === "simple" && !disabled) {
      const typeChanged = prevSimpleTypeRef.current !== simpleType;
      const optionsChanged = JSON.stringify(prevOptionsRef.current) !== JSON.stringify(options);
      
      if (typeChanged || optionsChanged) {
        const schema = simpleTypeToSchema();
        onChange(schema);
        setJsonText(JSON.stringify(schema, null, 2));
        
        prevSimpleTypeRef.current = simpleType;
        prevOptionsRef.current = options;
      }
    }
  }, [simpleType, options, mode, disabled, onChange, simpleTypeToSchema]);

  const handleModeChange = (newMode: EditorMode) => {
    if (!newMode) return;
    
    // If switching to simple mode from advanced with complex schema, warn user
    if (newMode === "simple" && mode === "advanced") {
      try {
        const currentSchema = JSON.parse(jsonText);
        const detectedType = detectSimpleType(currentSchema);
        
        // If schema is complex (can't be represented in simple mode), show warning
        if (!detectedType && Object.keys(currentSchema).length > 0) {
          setPendingMode(newMode);
          setModeChangeDialogOpen(true);
          return;
        }
      } catch (e) {
        // If JSON is invalid, just switch modes
      }
    }
    
    setMode(newMode);
  };

  const confirmModeChange = () => {
    if (pendingMode) {
      setMode(pendingMode);
      // Reset to default simple type
      setSimpleType("text");
      setOptions([]);
      setPendingMode(null);
    }
    setModeChangeDialogOpen(false);
  };

  const cancelModeChange = () => {
    setPendingMode(null);
    setModeChangeDialogOpen(false);
  };

  const handleJsonChange = (newText: string) => {
    setJsonText(newText);
    try {
      const parsed = JSON.parse(newText);
      setJsonError(null);
      onChange(parsed);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonText(formatted);
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const addOption = () => {
    setOptions([...options, ""]);
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const deleteOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {/* Mode Selector */}
      <Box display="flex" justifyContent="center" mb={2}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, newMode) => newMode && handleModeChange(newMode)}
          size="small"
          disabled={disabled}
        >
          <ToggleButton value="simple">
            <TuneIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <CodeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Advanced
          </ToggleButton>
          <ToggleButton value="preview">
            <VisibilityIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Preview
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Mode */}
      {mode === "simple" && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Field Type</InputLabel>
            <Select
              value={simpleType}
              label="Field Type"
              onChange={(e: SelectChangeEvent) =>
                setSimpleType(e.target.value as SimpleFieldType)
              }
              disabled={disabled}
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="number">Number</MenuItem>
              <MenuItem value="boolean">Yes/No (Boolean)</MenuItem>
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="time">Time</MenuItem>
              <MenuItem value="dropdown">Dropdown (Single Choice)</MenuItem>
              <MenuItem value="multiselect">Multi-Select (Checkboxes)</MenuItem>
            </Select>
          </FormControl>

          {/* Options for dropdown/multiselect */}
          {(simpleType === "dropdown" || simpleType === "multiselect") && (
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="caption" color="text.secondary">
                  Options
                </Typography>
                {!disabled && (
                  <Button size="small" onClick={addOption}>
                    Add Option
                  </Button>
                )}
              </Box>
              <Stack spacing={1}>
                {options.map((option, idx) => (
                  <Box key={idx} display="flex" gap={1}>
                    <TextField
                      value={option}
                      onChange={(e) => updateOption(idx, e.target.value)}
                      disabled={disabled}
                      size="small"
                      fullWidth
                      placeholder={`Option ${idx + 1}`}
                    />
                    {!disabled && (
                      <IconButton size="small" onClick={() => deleteOption(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              For complex schemas (nested objects, arrays, conditional fields), switch to <strong>Advanced</strong> mode.
            </Typography>
          </Alert>
        </Paper>
      )}

      {/* Advanced Mode */}
      {mode === "advanced" && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="caption" color="text.secondary">
              Edit JSON Schema directly
            </Typography>
            <Button size="small" onClick={formatJson} disabled={disabled}>
              Format JSON
            </Button>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={15}
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            disabled={disabled}
            error={!!jsonError}
            helperText={jsonError || "Enter valid JSON Schema"}
            sx={{
              fontFamily: "monospace",
              "& textarea": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
            }}
          />

          {!jsonError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Important:</strong> Ensure the JSON format is valid and supported by the form output creator. 
                Some models may not support all JSON Schema fields. Test the preview to verify the schema renders correctly.{" "}
                <a
                  href="https://json-schema.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  JSON Schema Documentation
                </a>
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* Preview Mode */}
      {mode === "preview" && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
          {jsonError ? (
            <Alert severity="error">Cannot preview: {jsonError}</Alert>
          ) : Object.keys(value || {}).length === 0 ? (
            <Alert severity="info">
              No schema defined yet. Switch to Simple or Advanced mode to define the schema.
            </Alert>
          ) : (
            <JsonSchemaAnswerRenderer schema={value} readonly={true} />
          )}
        </Paper>
      )}

      {/* Mode Change Warning Dialog */}
      <Dialog open={modeChangeDialogOpen} onClose={cancelModeChange}>
        <DialogTitle>Switch to Simple Mode?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current schema is complex and cannot be represented in Simple mode.
            Switching will replace it with a basic text field. This change cannot be undone.
            <br /><br />
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelModeChange}>Cancel</Button>
          <Button onClick={confirmModeChange} color="warning" variant="contained">
            Switch to Simple Mode
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HybridSchemaEditor;
